import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTenant } from '../config/tenantConfig';
import { useDarkMode } from '../DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { useSidePanel } from '../context/SidePanelContext';
import { usePermissions } from '../auth/usePermissions';
import { SIDEBAR_ITEM_KEYS } from '../auth/permissionsConfig';
import { setCompanyIdAndPersist } from '../slices/customerKYCThunks';
import Swal from 'sweetalert2';
import SupportChatAgent from '../components/SupportChatAgent';
import ReferralGuideAgent from '../components/ReferralGuideAgent';
import LandingGuideAgent from '../components/LandingGuideAgent';
import SystemStatusIndicator from '../components/SystemStatusIndicator';
import GlassTopBar from '../components/GlassTopBar';
import CollapsibleSidebar from '../components/CollapsibleSidebar';
import { buildCustomerPortalUrl } from '../config/customerPortalUrl';
import {
  Home,
  FileText,
  Users,
  CheckCircle,
  LogOut,
  Menu,
  X,
  Bell,
  Sun,
  Moon,
  TrendingUp,
  MessageSquare,
  DollarSign,
  BarChart3,
  Search as SearchIcon,
  Activity,
  FileCheck,
  Settings,
  Shield,
  ShieldCheck,
  Wallet
} from 'lucide-react';

const HALIAGENT_LOGO = `${process.env.PUBLIC_URL || ''}/assets/halitrade-logo-yellow.svg`;
const HALITRADE_LOGO = `${process.env.PUBLIC_URL || ''}/assets/halitrade-logo-red.svg`;

/**
 * Layout pour l'application HaliAgent
 * Interface moderne pour la gestion du commerce international
 */
const HaliTradeLayout = ({ children }) => {
  const { showSidePanel: isSidebarOpen, setShowSidePanel: setIsSidebarOpen, isMobile } = useSidePanel();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { tenantName, theme } = useTenant();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { user: authUser, isAuthenticated, logout } = useAuth();
  const currentUser = useSelector(state => state.userManagement.currentUser);
  const validationStats = useSelector(state => state.validation?.stats);
  const reduxCompanyId = useSelector(state => state.customerKYC?.companyId);

  // Sync user's company_id from auth to Redux to fix stale localStorage companyId
  useEffect(() => {
    if (isAuthenticated && authUser) {
      const userCompanyId = authUser.company_id || authUser.companyId;
      if (userCompanyId && userCompanyId !== reduxCompanyId) {
        console.log('[HaliAgentLayout] Syncing company_id from auth to Redux:', userCompanyId, '(was:', reduxCompanyId, ')');
        dispatch(setCompanyIdAndPersist(userCompanyId));
      }
    }
  }, [isAuthenticated, authUser, reduxCompanyId, dispatch]);
  const displayUser = currentUser || authUser || null;
  const displayName = displayUser
    ? `${displayUser.firstName || ''} ${displayUser.lastName || ''}`.trim() || displayUser.email
    : '';
  const displayCompany =
    displayUser?.companyName || displayUser?.company?.name || 'HaliAgent';
  const displayRole = displayUser?.role || displayUser?.category || 'Customer';
  const { hasAccessTo } = usePermissions();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // On mobile, sidebar overlays — no margin shift needed
  const sidebarWidth = isMobile ? 0 : (isSidebarOpen ? (sidebarCollapsed ? 80 : 256) : 0);
  const rawCategory = String(authUser?.category || authUser?.userType || '').toLowerCase();
  const isCustomerUser = rawCategory.includes('customer');
  
  // Determine if user is an agent (needed early for HaliSales chat initialization)
  // IMPORTANT: Agents can have category='halitrade' (tenant/app) with role='agent'.
  const roleLower = displayRole?.toLowerCase() || '';
  const categoryLower = rawCategory || '';
  
  // Explicit agent detection
  const hasAgentRole = roleLower.includes('agent') || 
                       roleLower.includes('broker') || 
                       roleLower.includes('referral');
  const hasAgentCategory = categoryLower === 'agent' || 
                           categoryLower === 'referral' || 
                           categoryLower === 'broker';
  const isPlatformUser = roleLower.includes('platform') || 
                         roleLower.includes('admin') ||
                         categoryLower === 'platform' ||
                         categoryLower === 'platform_user' ||
                         categoryLower === 'platform_admin';
  
  const isAgent = (hasAgentRole || hasAgentCategory) && !isPlatformUser;

  useEffect(() => {
    if (!isCustomerUser) return;
    if (!location.pathname.startsWith('/halitrade')) return;
    const targetPath = location.pathname.replace(/^\/halitrade/, '/customer');
    if (targetPath && targetPath !== location.pathname) {
      navigate(`${targetPath}${location.search}`, { replace: true });
    }
  }, [isCustomerUser, location.pathname, location.search, navigate]);


  // Menu items for agents - simple dashboard only
  const agentPrefix = isAgent ? '/haliagent' : '/halitrade';
  const agentMenuItems = [
    {
      icon: Home,
      label: 'Dashboard',
      path: `${agentPrefix}/dashboard`,
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.DASHBOARD
    },
    {
      icon: FileText,
      label: 'Submit Opportunity',
      path: `${agentPrefix}/invoice-onboarding`,
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.BUYERS
    },
    {
      icon: Users,
      label: 'Trade Partners',
      path: `${agentPrefix}/trade-partners`,
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.SUPPLIERS
    },
    {
      icon: Users,
      label: 'My Submissions',
      path: `${agentPrefix}/my-submissions`,
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.AGENTS
    },
    {
      icon: FileCheck,
      label: 'My Termsheets',
      path: `${agentPrefix}/halidoc`,
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.LEGAL_DOCS
    },
    {
      icon: Wallet,
      label: 'My Wallet',
      path: `${agentPrefix}/wallet`,
      badge: null,
      itemKey: null  // Always visible for agents - no permission check needed
    },
    {
      icon: DollarSign,
      label: 'Subscription',
      path: buildCustomerPortalUrl('/subscription'),
      badge: null,
      itemKey: null,
      external: true  // Opens in new tab
    },
    {
      icon: Settings,
      label: 'Settings',
      path: `${agentPrefix}/settings`,
      badge: null,
      itemKey: null  // Always visible for agents - no permission check needed
    }
  ];

  // Menu items for platform users - full access
  const platformMenuItems = [
    {
      icon: Home,
      label: 'Dashboard',
      path: '/halitrade/dashboard',
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.DASHBOARD
    },
    {
      icon: TrendingUp,
      label: 'Factoring Tools',
      path: '/halitrade/factor-tools',
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.INVOICES
    },
    {
      icon: ShieldCheck,
      label: 'Risk Analysis',
      path: '/halitrade/risk-analysis',
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.COMPLIANCE_CHECKS
    },
    {
      icon: Shield,
      label: 'HaliRisk v2',
      path: '/halitrade/halirisk-playbook',
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.COMPLIANCE_CHECKS
    },
    {
      icon: BarChart3,
      label: 'Supply Chain Finance',
      path: '/halitrade/supply-chain',
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.PAST_TRADES
    },
    {
      icon: DollarSign,
      label: 'Invoice Financing',
      path: '/halitrade/invoice-financing',
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.INVOICES
    },
    {
      icon: MessageSquare,
      label: 'Messages',
      path: '/halitrade/messages',
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.OPERATIONS
    },
    {
      icon: FileText,
      label: 'My Company',
      path: '/halitrade/company',
      badge: validationStats?.revisionRequired > 0 ? validationStats.revisionRequired : null,
      itemKey: SIDEBAR_ITEM_KEYS.OPERATIONS
    },
    {
      icon: Users,
      label: 'Trade Partners',
      path: '/halitrade/trade-partners',
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.SUPPLIERS
    },
    {
      icon: CheckCircle,
      label: 'Validation Status',
      path: '/halitrade/validation-status',
      badge: validationStats?.pending > 0 ? validationStats.pending : null,
      itemKey: SIDEBAR_ITEM_KEYS.COMPLIANCE_CHECKS
    },
    {
      icon: FileCheck,
      label: 'LC DocCheck',
      path: '/halitrade/lctradedoccheck',
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.COMPLIANCE_CHECKS
    },
    {
      icon: Activity,
      label: 'Debug Console',
      path: '/halitrade/debug',
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.OPERATIONS
    },
    {
      icon: DollarSign,
      label: 'Subscription',
      path: buildCustomerPortalUrl('/subscription'),
      badge: null,
      itemKey: null,
      external: true  // Opens in new tab
    },
    {
      icon: Settings,
      label: 'Settings',
      path: '/halitrade/settings/permissions',
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.SETTINGS
    }
  ];

  // Determine which menu to show based on user role (isAgent already defined above)
  console.log('[HaliTradeLayout] User detection:', {
    displayRole,
    rawCategory,
    hasAgentRole,
    hasAgentCategory,
    isPlatformUser,
    isAgent
  });
  const brandName = isAgent ? 'HaliAgent' : 'HaliTrade';
  const brandLogo = isAgent ? HALIAGENT_LOGO : HALITRADE_LOGO;
  const viewBadge = isAgent ? 'Agent View' : (isPlatformUser ? 'Platform View' : 'Customer View');
  const menuItems = isAgent ? agentMenuItems : platformMenuItems;
  const visibleMenuItems = menuItems.filter((item) => !item.itemKey || hasAccessTo(item.itemKey));
  const iconColorMap = {
    'Dashboard': '#60a5fa',
    'Submit Opportunity': '#a855f7',
    'My Submissions': '#34d399',
    'My Termsheets': '#22d3ee',
    'My Wallet': '#14b8a6',
    'Factoring Tools': '#facc15',
    'Risk Analysis': '#10b981',
    'HaliRisk v2': '#e11d48',
    'Supply Chain Finance': '#f472b6',
    'Invoice Financing': '#facc15',
    'Messages': '#34d399',
    'My Company': '#22d3ee',
    'Trade Partners': '#34d399',
    'Validation Status': '#ef4444',
    'Debug Console': '#a855f7',
    Settings: '#f97316'
  };
  const sidebarMenuItems = visibleMenuItems.map((item) => ({
    ...item,
    color: iconColorMap[item.label] || theme.primary
  }));

  const handleSidebarNavigate = (path) => {
    navigate(path);
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleBrandClick = () => {
    const prefix = isAgent ? '/haliagent' : '/halitrade';
    navigate(`${prefix}/dashboard`);
  };

  const handleNotificationsClick = () => {
    setIsChatOpen((prev) => !prev);
  };

  // Tenant guard: redirect users who don't belong to HaliTrade
  // Debounce tenant check to prevent race conditions during login state propagation
  useEffect(() => {
    console.log('[HaliAgentLayout] Tenant guard check - isAuthenticated:', isAuthenticated, 'user category:', authUser?.category, 'tenant:', authUser?.tenant);

    if (isAuthenticated && authUser) {
      // Give a small delay to allow auth state to fully propagate after login
      const timeoutId = setTimeout(() => {
        const userCategory = (authUser.category || authUser.role || '').toLowerCase();
        const userTenant = (authUser.tenant || '').toLowerCase();
        // Allow halitrade/haliagent users AND platform users (platform can access HaliAgent for validation)
        const isHaliAgentUser = userCategory === 'halitrade' ||
                                 userCategory === 'halitrade_user' ||
                                 userCategory === 'platform' ||
                                 userCategory === 'platform_user' ||
                                 userCategory === 'platform_admin' ||
                                 userTenant === 'halitrade';

        if (!isHaliAgentUser) {
          console.log('[HaliAgentLayout] ❌ User does not belong to HaliAgent tenant.');
          console.log('[HaliAgentLayout] User category:', userCategory);
          console.log('[HaliAgentLayout] User tenant:', userTenant);
          console.log('[HaliAgentLayout] Full user:', authUser);
          console.log('[HaliAgentLayout] Logging out and redirecting...');

          // Logout and redirect based on user type
          const wasAgent = userCategory.includes('agent') || userCategory.includes('broker') || userCategory.includes('referral');
          logout();
          setTimeout(() => {
            navigate(wasAgent ? '/haliagent-landing' : '/halitrade-landing', { replace: true });
          }, 150);
        } else {
          console.log('[HaliAgentLayout] ✅ Valid HaliAgent user');
        }
      }, 100); // Small delay to allow state to propagate

      return () => clearTimeout(timeoutId);
    } else if (!isAuthenticated) {
      console.log('[HaliAgentLayout] Not authenticated - showing login page');
    }
  }, [isAuthenticated, authUser, logout, navigate]);

  useEffect(() => {
    if (!isSidebarOpen) {
      setSidebarCollapsed(false);
    }
  }, [isSidebarOpen]);

  const handleLogout = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Confirm logout',
      text: 'Are you sure you want to exit HaliAgent?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, exit',
      cancelButtonText: 'Stay',
      reverseButtons: true,
      background: darkMode ? '#1F2937' : '#fff',
      color: darkMode ? '#F9FAFB' : '#111827',
      confirmButtonColor: theme.primary
    });

    if (isConfirmed) {
      try {
        // Save agent status BEFORE logout (user data will be cleared after logout)
        const wasAgent = isAgent;
        await logout();
        // Redirect agents to haliagent-landing, platform users to halitrade-landing
        navigate(wasAgent ? '/haliagent-landing' : '/halitrade-landing');
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <GlassTopBar
        logoSrc={brandLogo}
        brandName={brandName}
        brandSubtitle="powered by HaliSoft"
        displayRole={displayRole}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        isSidebarCollapsed={!isSidebarOpen}
        onSidebarToggle={handleSidebarToggle}
        onBrandClick={handleBrandClick}
        notifications={(validationStats?.pending || 0) + (validationStats?.revisionRequired || 0)}
        onNotificationsClick={handleNotificationsClick}
        onLogout={handleLogout}
        borderColor={theme.primary + '20'}
      />

      <div className="flex">
        {/* Sidebar */}
        <CollapsibleSidebar
          className={!isSidebarOpen ? 'sidebar-hidden' : ''}
          style={{ top: '96px' }}
          logoSrc={brandLogo}
          brandName={brandName}
          platformName={brandName}
          subtitle="Powered by HaliSoft"
          viewBadge={viewBadge}
          menuItems={sidebarMenuItems}
          activePath={location.pathname}
          onNavigate={handleSidebarNavigate}
          onLogoClick={() => navigate(`${isAgent ? '/haliagent' : '/halitrade'}/dashboard`)}
          onToggle={(collapsed) => setSidebarCollapsed(collapsed)}
          primaryColor={theme.primary}
          darkMode={darkMode}
        />

        {/* Main Content */}
        <main
          className="flex-1 overflow-auto transition-all duration-300 ease-in-out"
          style={{ marginLeft: sidebarWidth }}
        >
          <div className="p-6 lg:p-8">
            <div className="glass-surface glass-panel rounded-[28px] p-6 lg:p-8 shadow-[0_35px_120px_rgba(15,23,42,0.15)]">
              {children || <Outlet />}
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Support Chat Agent - Always mounted to listen for events from ReferralGuideAgent */}
      <SupportChatAgent onOpenChange={setIsChatOpen} />

      {/* Referral Guide Agent - Only show for agents */}
      {isAgent && (
        <ReferralGuideAgent />
      )}

      <LandingGuideAgent darkMode={darkMode} theme="haliagent" position="left" />
    </div>
  );
};

export default HaliTradeLayout;
